import { useSEO } from "@/hooks/use-seo";
import { metaDescription } from "@shared/seo-routes";
import { Link, useParams, useLocation } from "wouter";
import { useLanguage } from "@/contexts/LanguageContext";
import { articleImages } from "@/data/article-images";
import { articles } from "@/data/articles";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ExpertContact from "@/components/ExpertContact";
import { Calendar, Clock, ArrowLeft, ArrowRight, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import basLensImg from "@assets/bas-lens-922x1024_1771928334746.jpg";
import robinImg from "@assets/1678270794100_1772030399622.jpeg";

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [, setLocation] = useLocation();
  const { language, localePath } = useLanguage();
  const nl = language === "nl";

  const article = articles.find((a) => a.slug === slug);
  const articleIndex = articles.findIndex((a) => a.slug === slug);

  const relatedArticles = articles
    .filter((a) => a.slug !== slug)
    .sort(() => 0.5 - Math.random())
    .slice(0, 3);

  useSEO({
    title: article
      ? `${nl ? (article.seoTitle ?? article.title) : (article.seoTitleEn ?? article.titleEn)} | Wrkt Digital`
      : "Artikel niet gevonden | Wrkt Digital",
    description: article ? metaDescription(nl ? article.excerpt : article.excerptEn) : "",
    path: `/artikelen/${slug}`,
    lang: nl ? "nl" : "en",
    keywords: article ? (nl ? article.tags : article.tagsEn).join(", ") : "",
  });

  if (!article) {
    return (
      <div className="min-h-screen bg-[#0A1F14] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-display font-bold mb-4">404</h1>
          <p className="text-white/60 mb-8">
            {nl ? "Artikel niet gevonden" : "Article not found"}
          </p>
          <Button onClick={() => setLocation(localePath("/artikelen"))}>
            {nl ? "Terug naar artikelen" : "Back to articles"}
          </Button>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(nl ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const prevArticle = articleIndex > 0 ? articles[articleIndex - 1] : null;
  const nextArticle =
    articleIndex < articles.length - 1 ? articles[articleIndex + 1] : null;

  return (
    <div className="min-h-screen bg-[#0A1F14] text-white font-sans">
      <Navbar activePage="artikelen" />



      <section className="relative pt-24">
        <div className="w-full h-[300px] md:h-[450px] relative overflow-hidden">
          <img
            src={articleImages[article.slug]}
            alt={nl ? article.title : article.titleEn}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F14] via-[#0A1F14]/50 to-[#0A1F14]/20"></div>
        </div>
      </section>

      <section className="relative -mt-32 z-10 pb-16">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl">
          <Link
            href={localePath("/artikelen")}
            className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-[#4CEAAC] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {nl ? "Alle artikelen" : "All articles"}
          </Link>

          <div className="flex flex-wrap gap-2 mb-4">
            {(nl ? article.tags : article.tagsEn).map((tag) => (
              <span
                key={tag}
                className="text-[10px] font-bold uppercase tracking-widest text-[#4CEAAC]/70 bg-[#4CEAAC]/10 px-2.5 py-1 rounded"
              >
                {tag}
              </span>
            ))}
          </div>

          <h1 className="text-3xl md:text-5xl font-display font-bold leading-tight mb-6">
            {nl ? article.title : article.titleEn}
          </h1>

          <div className="flex items-center gap-6 text-sm text-white/40 mb-12 pb-8 border-b border-white/10">
            <div className="flex items-center gap-3">
              <img
                src={article.author === "Robin de Wildt" ? robinImg : basLensImg}
                alt={article.author}
                className="w-10 h-10 rounded-full object-cover border border-white/10"
              />
              <div>
                <span className="text-white/70 font-medium block text-sm">
                  {article.author}
                </span>
                <span className="text-white/30 text-xs">
                  {article.authorRole}
                </span>
              </div>
            </div>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {formatDate(article.date)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              {article.readTime} min read
            </span>
          </div>

          <div
            className="article-content prose prose-invert prose-lg max-w-none
              [&>p]:text-white/70 [&>p]:leading-relaxed [&>p]:mb-6
              [&>h2]:text-2xl [&>h2]:md:text-3xl [&>h2]:font-display [&>h2]:font-bold [&>h2]:text-white [&>h2]:mt-12 [&>h2]:mb-4
              [&>h3]:text-xl [&>h3]:font-display [&>h3]:font-bold [&>h3]:text-white [&>h3]:mt-8 [&>h3]:mb-3
              [&>ul]:text-white/70 [&>ul]:space-y-2 [&>ul]:mb-6 [&>ul>li]:pl-2
              [&>ol]:text-white/70 [&>ol]:space-y-2 [&>ol]:mb-6
              [&_a]:text-[#4CEAAC] [&_a]:no-underline [&_a:hover]:underline
              [&_strong]:text-white/90
              [&_blockquote]:border-l-2 [&_blockquote]:border-[#4CEAAC]/30 [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-white/50"
            dangerouslySetInnerHTML={{ __html: nl ? article.content : article.contentEn }}
          />

          <div className="mt-16 p-8 bg-white/[0.03] border border-white/10 rounded-2xl flex items-start gap-6">
            <img
              src={article.author === "Robin de Wildt" ? robinImg : basLensImg}
              alt={article.author}
              loading="lazy"
              decoding="async"
              className="w-16 h-16 rounded-full object-cover border-2 border-[#4CEAAC]/20 flex-shrink-0"
            />
            <div>
              <p className="font-display font-bold text-lg mb-1">
                {article.author}
              </p>
              <p className="text-[#4CEAAC] text-sm mb-3">
                {article.authorRole}
              </p>
              <p className="text-white/50 text-sm leading-relaxed">
                {article.author === "Robin de Wildt"
                  ? nl
                    ? "Robin is Digital Growth Expert bij Wrkt Digital. Met een passie voor D2C branding en data-driven strategie helpt ze merken groeien op basis van bewijs, niet op onderbuikgevoel."
                    : "Robin is a Digital Growth Expert at Wrkt Digital. With a passion for D2C branding and data-driven strategy, she helps brands grow based on evidence, not gut feeling."
                  : nl
                    ? "Bas is oprichter van Wrkt Digital, een AI-first growth agency in Amsterdam dat D2C e-commerce merken helpt schalen met data-driven performance marketing."
                    : "Bas is the founder of Wrkt Digital, an AI-first growth agency in Amsterdam that helps D2C e-commerce brands scale with data-driven performance marketing."}
              </p>
            </div>
          </div>

          <div className="mt-8 flex justify-between items-center gap-4">
            {prevArticle ? (
              <Link
                href={localePath(`/artikelen/${prevArticle.slug}`)}
                className="flex items-center gap-2 text-sm text-white/40 hover:text-[#4CEAAC] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">{(nl ? prevArticle.title : prevArticle.titleEn).substring(0, 40)}...</span>
                <span className="sm:hidden">{nl ? "Vorige" : "Previous"}</span>
              </Link>
            ) : (
              <div />
            )}
            {nextArticle ? (
              <Link
                href={localePath(`/artikelen/${nextArticle.slug}`)}
                className="flex items-center gap-2 text-sm text-white/40 hover:text-[#4CEAAC] transition-colors text-right"
              >
                <span className="hidden sm:inline">{(nl ? nextArticle.title : nextArticle.titleEn).substring(0, 40)}...</span>
                <span className="sm:hidden">{nl ? "Volgende" : "Next"}</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <div />
            )}
          </div>
        </div>
      </section>

      <section className="py-20 border-t border-white/5">
        <div className="container mx-auto px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-display font-bold mb-10 text-center">
            {nl ? "Meer artikelen" : "More articles"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {relatedArticles.map((a) => (
              <Link key={a.slug} href={localePath(`/artikelen/${a.slug}`)}>
                <article className="group bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden hover:border-[#4CEAAC]/30 transition-all duration-500 cursor-pointer">
                  <div className="h-40 overflow-hidden">
                    <img
                      src={articleImages[a.slug]}
                      alt={nl ? a.title : a.titleEn}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex gap-2 mb-2">
                      {(nl ? a.tags : a.tagsEn).slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="text-[9px] font-bold uppercase tracking-widest text-[#4CEAAC]/60 bg-[#4CEAAC]/10 px-2 py-0.5 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="text-sm font-display font-bold leading-snug group-hover:text-[#4CEAAC] transition-colors">
                      {nl ? a.title : a.titleEn}
                    </h3>
                    <div className="flex items-center gap-3 mt-3 text-[11px] text-white/30">
                      <span>{formatDate(a.date)}</span>
                      <span>{a.readTime} min</span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ExpertContact />

      <Footer />
    </div>
  );
}
